import { store } from "../../../main";
import { determineIdentifierCodeType } from "../../../actions/validation";
import { formBIC, localDate, maxTwoDecimals } from "../../../common/utils";

function getCompanyFromRedux() {
  const auth = store?.getState().auth; // redux
  return {
    address1: auth.companyAddress1,
    address2: auth.companyAddress2,
    address3: auth.companyAddress3,
    businessId: auth.companyBusinessId,
    email: auth.companyEmail,
    logoUrl: auth.company?.logoUrl ? auth.company.logoUrl : auth.companyLogoUrl, // prefer organization logo over auth token (eSalary) logo
    name: auth.companyName,
    phone: auth.companyPhone,
  };
}

export const initialState = {

  contractId: null,
  identifier: "",
  personId: "",
  date: localDate(),
  status: "DRAFT",
  modelName: "",
  newPerson: false,
  newModel: false,

  person: {
    nr: "",
    name: "",
    forename: "",
    surname: "",
    callingName: "",
    socSec: "",
    gender: "M",

    streetAddress: "",
    postalCode: "",
    postalAddress: "",
    country: "",
    tel: "",
    iban: "",
    bic: "",
    startDate: "",
  },
  details: {
    startDate: "",
    endDate: "",
    endDateShadow: "",
    fixedDuration: true,
    fixedDurationExplanation: "",
    fixedDurationExplanationShadow: "",
    trialPeriod: true,
    trialPeriodLength: 6,
    trialPeriodLengthShadow: "",
    trialPeriodEndDate: "",
    trialPeriodExplanation: "",
    trialPeriodExplanationShadow: "",
    termOfNotice: "",
    dailyWorkHours: "",
    weeklyWorkHours: "",
    workHours: "",
    workCycle: "",
    workHoursInformation: "",
    vacationInformation: "",
    trialPeriodEndDateShadow: "",
    jobTitle: "",
    place: "",
    assignment: "",
    cbaCode: "",
    occupationCodeTK10: "",
    occupationCodeKeva: "",
    signer: "",
    signDate: "",
    language: "fi",
  },
  pay: {
    amount: "",
    currency: "EUR",
    period: "",
    day: "",
    basis: "TIME",
    otherBasis: "",
    formOfSalary: "1",
    employmentType: "1",
    phoneBenefit: false,
    bicycleBenefit: false,
    mealBenefit: false,
    bicycleBenefitSubstracts: false,
    mealBenefitTaxableValue: false,
    personalBenefit1: 0,
    personalBenefit2: 0,
    carUsageBenefit: true,
    freeCarBenefit: false,
  },
  terms: [],
};

export function calcTrialPeriodEndDate(startDate, trialPeriodLength, trialPeriodLengthDays) {
  //DateTime.fromISO(startDate).plus({ months: trialPeriodLength}).minus({ days: 1 }).toISODate(); // luxon
  if (!startDate || !trialPeriodLength) return "";
  trialPeriodLength = Number(trialPeriodLength);
  if ( isNaN(trialPeriodLength) ) return "";

  const date = new Date(startDate);

  if (trialPeriodLengthDays) {
    date.setUTCDate(date.getUTCDate() + trialPeriodLength);
  } else {
    date.setUTCMonth(date.getUTCMonth() + trialPeriodLength);
    date.setUTCDate(date.getUTCDate() - 1);
  }

  if (!isNaN(date)) {
    return localDate(date); // YYYY-MM-DD
  }
}

export function reducer(state, action) {
  switch(action.type) {

    case "initialState": {
      const company = getCompanyFromRedux();
      return { ...initialState, company: company };
    }

    case "open_for_editing":
      return { ...state, ...action.payload,
        details: {
          ...action.payload.details,
          endDateShadow: "",
          fixedDurationExplanationShadow: "",
          trialPeriodLengthShadow: "",
          trialPeriodEndDateShadow: "",
        } };

    case "copy_person_details": {
      // copy employmentDetails only when the employment relationship is not ended
      const employmentDetails = action.payload.employmentDetails.endDate ? null : action.payload.employmentDetails;
      return {
        ...state,
        personId: action.payload.personId,
        person: {
          ...state.person,
          ...action.payload,
          ...action.payload.details,
          ...employmentDetails,
        },
        details: {
          ...state.details,
          jobTitle: action.payload && action.payload.details && action.payload.details.profession
            ? action.payload.details.profession : ""
        },
        company: action.payload.company ? {
          businessId: action.payload.company.businessIdentifier,
          name: action.payload.company.organizationName,
          address1: action.payload.company.organizationDetails?.streetAddress || "",
          address2: action.payload.company.organizationDetails ? `${action.payload.company.organizationDetails.postalCode} ${action.payload.company.organizationDetails.postalAddress}` : "",
          address3: action.payload.company.organizationDetails?.country || "",
          email: action.payload.company.organizationDetails?.email || "",
          phone: action.payload.company.organizationDetails?.phone || "",
          url: action.payload.company.organizationDetails?.www || "",
          logoUrl: action.payload.company.organizationDetails?.image || "",
        } : state.company, // if company details are attached to person, copy them to contract, otherwise use default company details from redux (logged in user's company)
      };
    }

    case "copy_for_editing": {
      const company = getCompanyFromRedux();
      return { ...state, ...action.payload,
        contractId: null,
        attachments: null, // remove attachments from copy
        identifier: "",
        date: localDate(),
        personId: "",
        newPerson: true,
        newModel: false,
        modelName: "",
        status: "DRAFT",
        person: {
          nr: "",
          name: "",
          forename: "",
          surname: "",
          callingName: "",
          socSec: "",
          gender: "M",
          streetAddress: "",
          postalCode: "",
          postalAddress: "",
          country: "",
          tel: "",
          iban: "",
          bic: "",
          startDate: "",
        },
        details: {
          ...action.payload.details,
          startDate: "",
          endDate: "",
          trialPeriodEndDate: "",
          endDateShadow: "",
          fixedDurationExplanationShadow: "",
          trialPeriodLengthShadow: "",
          trialPeriodEndDateShadow: "",
        },
        company: company
      };
    }

    case "identifier": return { ...state, identifier: action.payload };
    case "personId": return { ...state, personId: action.payload };
    case "date": return { ...state, date: action.payload };
    case "modelName": return { ...state, modelName: action.payload };
    case "status": return { ...state, status: action.payload };

    case "newPerson.toggle": return { ...state, newPerson: !state.newPerson };
    case "newModel.toggle": return { ...state, newModel: !state.newModel, status: !state.newModel ? "MODEL" : "DRAFT", modelName: !state.newModel ? "" : "" };

    case "nr":
    case "name":
    case "forename":
    case "surname":
    case "callingName":
    case "gender":
    case "streetAddress":
    case "postalCode":
    case "postalAddress":
    case "country":
    case "tel":
    case "bic":
      return { ...state, person: { ...state.person, [action.type]: action.payload } } ;

    case "personEsalaryDisabled":
    case "personPsaDisabled":
      return { ...state, person: { ...state.person, settings: {
        ...state.person.settings,
        [action.type]: action.payload ? "1" : "0",
      } } };

    case "set_person_teams":
      return {
        ...state,
        person: {
          ...state.person,
          teams: action.payload,
          controlDetails: {
            ...state.person.controlDetails,
            teams: action.payload,
          },
        },
      };

    case "socSec": {
      const newState = { ...state, person: { ...state.person, [action.type]: action.payload } };

      const newGender = determineIdentifierCodeType(action.payload);
      if (newGender) {
        newState.person.gender = newGender;
        action.formMethods.setValue("gender", newGender); // set uncontrolled input value
      }

      return newState;
    }

    case "iban": {
      const bic = formBIC(action.payload);
      if (bic !== null) {
        return { ...state, person: { ...state.person, [action.type]: action.payload, bic: bic } };
      } else {
        return { ...state, person: { ...state.person, [action.type]: action.payload } };
      }
    }

    case "personStartDate":
      return { ...state,
        details: {
          ...state.details,
          startDate: action.payload,
          trialPeriodEndDate: calcTrialPeriodEndDate(action.payload, state.details.trialPeriodLength, state.details.trialPeriodLengthDays),
        },
        person: { ...state.person, startDate: action.payload } } ;

    case "fixedDuration.false":
      return { ...state, details: {
        ...state.details,
        fixedDuration: false,
        endDate: state.details.endDateShadow,
        endDateShadow: state.details.endDate,
        fixedDurationExplanationShadow: state.details.fixedDurationExplanation,
        fixedDurationExplanation: state.details.fixedDurationExplanationShadow
      } };
    case "fixedDuration.true":
      return { ...state, details: {
        ...state.details,
        fixedDuration: true,
        endDate: state.details.endDateShadow,
        endDateShadow: state.details.endDate,
        fixedDurationExplanationShadow: state.details.fixedDurationExplanation,
        fixedDurationExplanation: state.details.fixedDurationExplanationShadow
      } };
    case "trialPeriod.toggle":
      return { ...state, details: {
        ...state.details,
        trialPeriod: !state.details.trialPeriod,
        trialPeriodLength: state.details.trialPeriodLengthShadow,
        trialPeriodLengthShadow: state.details.trialPeriodLength,
        trialPeriodEndDate: !state.details.trialPeriod ? calcTrialPeriodEndDate(state.details.startDate ? state.details.startDate : state.person.startDate, state.details.trialPeriodLengthShadow, state.details.trialPeriodLengthDays) : "",
        trialPeriodEndDateShadow: state.details.trialPeriodEndDate
      } };
    case "trialPeriodLengthDays.toggle": {
      const newTrialPeriodLengthDays = !state.details.trialPeriodLengthDays;
      return {
        ...state,
        details: {
          ...state.details,
          trialPeriodLengthDays: newTrialPeriodLengthDays,
          trialPeriodLength: state.details.trialPeriodLength,
          trialPeriodEndDate: calcTrialPeriodEndDate(
            state.details.startDate || state.person.startDate,
            state.details.trialPeriodLength,
            newTrialPeriodLengthDays
          ),
        }
      };
    }

    case "phoneBenefit.toggle": return { ...state, pay: { ...state.pay, phoneBenefit: !state.pay.phoneBenefit } };
    case "bicycleBenefit.toggle": return { ...state, pay: { ...state.pay, bicycleBenefit: !state.pay.bicycleBenefit } };
    case "bicycleBenefitSubstracts.toggle": return { ...state, pay: { ...state.pay, bicycleBenefitSubstracts: !state.pay.bicycleBenefitSubstracts } };
    case "mealBenefit.toggle": return { ...state, pay: { ...state.pay, mealBenefit: !state.pay.mealBenefit } };
    case "mealBenefitTaxableValue.toggle": return { ...state, pay: { ...state.pay, mealBenefitTaxableValue: !state.pay.mealBenefitTaxableValue } };
    case "carUsageBenefit.toggle": return { ...state, pay: { ...state.pay, carUsageBenefit: !state.pay.carUsageBenefit, freeCarBenefit: !state.pay.carUsageBenefit ? false : true } };
    case "freeCarBenefit.toggle": return { ...state, pay: { ...state.pay, freeCarBenefit: !state.pay.freeCarBenefit, carUsageBenefit: !state.pay.freeCarBenefit ? false: true } };

    case "startDate": return { ...state,
      person: { ...state.person, startDate: state.person.startDate ? state.person.startDate : action.payload },
      details: {
        ...state.details,
        startDate: action.payload,
        trialPeriodEndDate: calcTrialPeriodEndDate(action.payload, state.details.trialPeriodLength),
      } };
    case "endDate": return { ...state, details: { ...state.details, endDate: action.payload } };
    case "fixedDurationExplanation": return { ...state, details: { ...state.details, fixedDurationExplanation: action.payload } };
    case "trialPeriodExplanation": return { ...state, details: { ...state.details, trialPeriodExplanation: action.payload } };
    case "trialPeriodLength": return { ...state, details: {
      ...state.details,
      trialPeriodLength: action.payload,
      trialPeriodEndDate: calcTrialPeriodEndDate(state.details.startDate ? state.details.startDate : state.person.startDate, action.payload, state.details.trialPeriodLengthDays),
    } };

    case "trialPeriodEndDate":
    case "jobTitle":
    case "place":
    case "assignment":
    case "cbaCode":
    case "occupationCodeTK10":
    case "occupationCodeKeva":
    case "language":
    case "signer":
    case "termOfNotice":
    case "dailyWorkHours":
    case "weeklyWorkHours":
    case "workHours":
    case "workCycle":
    case "workHoursInformation":
    case "vacationInformation":
    case "signDate":
      return { ...state, details: { ...state.details, [action.type]: action.payload } };

    case "amount": {
      let calculatedSalary = maxTwoDecimals(action.payload * state.pay.paymentPercentage / 100);
      return {
        ...state,
        pay: {
          ...state.pay,
          [action.type]: action.payload,
          paymentPercentageSalary: calculatedSalary,
        }
      };
    }
    case "paymentPercentage": {
      let calculatedSalary = maxTwoDecimals(action.payload * state.pay.amount / 100);
      return {
        ...state,
        pay: {
          ...state.pay,
          [action.type]: action.payload,
          paymentPercentageSalary: calculatedSalary,
        }
      };
    }

    case "period":
    case "day":
    case "currency":
    case "employmentType":
    case "formOfSalary":
    case "partTimePercentage":
    case "paymentPercentageSalary":
    case "personalBenefit1":
    case "otherBenefits":
    case "payUnit":
      return { ...state, pay: { ...state.pay, [action.type]: action.payload } };
    case "basis":
      return { ...state, pay: {
        ...state.pay,
        basis: action.payload,
        otherBasis: action.payload !== "3" ? "" : state.pay.otherBasisShadow
      } };
    case "otherBasis":
      return { ...state, pay: {
        ...state.pay,
        otherBasis: action.payload,
        otherBasisShadow: action.payload
      } };

    case "terms":
      return { ...state, terms: action.payload };

    case "personalBenefit2": {
      if (action.payload === "1") {
        return { ...state, pay: { ...state.pay, [action.type]: action.payload, carUsageBenefit: true } };
      }
      else {
        return { ...state, pay: { ...state.pay, [action.type]: action.payload, carUsageBenefit: null, freeCarBenefit: null } };
      }
    }

    default:
      throw new Error(action);
  }

}
